import {
  ArrowClockwiseIcon,
  CheckCircleIcon,
  FileIcon,
  ImageIcon,
  VideoCameraIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Link } from 'react-router';
import { formatBytes } from '../../lib/format';
import { cancelUpload, retryUpload, type UploadEntry } from '../../lib/uploads';
import { IconButton, ListGroup, ProgressBar } from '../../ui';

const KIND_ICON = { image: ImageIcon, video: VideoCameraIcon, file: FileIcon } as const;

export function UploadList({ entries }: { entries: UploadEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <ListGroup>
      {entries.map((e) => {
        const IconCmp = KIND_ICON[e.kind];
        return (
          <li key={e.key} className="flex items-center gap-3 px-4 py-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sheet-2 text-ink-2">
              <IconCmp size={22} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[0.9375rem] font-medium text-ink">{e.fileName}</p>
              {e.status === 'uploading' ? (
                <>
                  <ProgressBar
                    value={e.progress}
                    className="mt-1.5"
                    label={`Caricamento di ${e.fileName}`}
                  />
                  <p className="tabular mt-1 text-xs text-ink-3">
                    {Math.round(e.progress * 100)}% di {formatBytes(e.size)}
                    {e.resumed ? ', ripreso da dove era arrivato' : ''}
                  </p>
                </>
              ) : e.status === 'done' ? (
                <p className="text-sm text-ok">
                  Caricato in {e.pageName}
                  {e.kind !== 'file' ? ', preparo l’anteprima' : ''}
                </p>
              ) : e.status === 'error' ? (
                <p className="text-sm text-alert">{e.error}</p>
              ) : (
                <p className="text-sm text-ink-3">Annullato</p>
              )}
            </div>
            {e.status === 'uploading' ? (
              <IconButton
                icon={XIcon}
                label="Annulla caricamento"
                size="sm"
                onClick={() => cancelUpload(e.key)}
              />
            ) : e.status === 'error' ? (
              <IconButton
                icon={ArrowClockwiseIcon}
                label="Riprova"
                size="sm"
                onClick={() => retryUpload(e.key)}
              />
            ) : e.status === 'done' ? (
              <CheckCircleIcon size={24} weight="fill" className="text-ok" aria-label="Fatto" />
            ) : (
              <WarningCircleIcon size={22} className="text-ink-3" aria-hidden />
            )}
          </li>
        );
      })}
    </ListGroup>
  );
}

/** Pillola sopra la barra in basso: mostra i caricamenti in corso da qualsiasi pagina. */
export function UploadIndicator({ entries }: { entries: UploadEntry[] }) {
  const running = entries.filter((e) => e.status === 'uploading');
  if (running.length === 0) return null;
  const total = running.reduce((s, e) => s + e.size, 0);
  const sent = running.reduce((s, e) => s + e.size * e.progress, 0);
  const pct = total ? sent / total : 0;
  return (
    <Link
      to="/carica"
      className="fixed inset-x-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-40 mx-auto flex max-w-md items-center gap-3 rounded-2xl bg-ink px-4 py-3 text-sheet shadow-[var(--shadow-lift)] lg:right-8 lg:bottom-8 lg:left-auto lg:w-80"
    >
      <span className="relative flex h-9 w-9 shrink-0 items-center justify-center">
        <svg viewBox="0 0 36 36" className="h-9 w-9 -rotate-90" aria-hidden>
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.25"
            strokeWidth="3"
          />
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${pct * 94.25} 94.25`}
          />
        </svg>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">
          {running.length === 1 ? 'Carico 1 file' : `Carico ${running.length} file`}
        </span>
        <span className="tabular block text-xs opacity-80">
          {Math.round(pct * 100)}% di {formatBytes(total)}
        </span>
      </span>
    </Link>
  );
}
